import React from 'react';
import { Loader2 } from 'lucide-react';

export const AddonsRevenueTab = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--isp-body)]" />
        <span className="ml-2 text-[var(--isp-body)]">Loading revenue data...</span>
      </div>
    );
  }

  const breakdown = data.addon_breakdown ? Object.values(data.addon_breakdown) : [];

  return (
    <div className="space-y-5" data-testid="addons-revenue-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 text-center">
          <p className="text-3xl font-bold text-emerald-400">${data.mrr}</p>
          <p className="text-xs text-[var(--isp-muted)] mt-1">Monthly Recurring Revenue</p>
        </div>
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-4 text-center">
          <p className="text-3xl font-bold text-cyan-400">${data.arr}</p>
          <p className="text-xs text-[var(--isp-muted)] mt-1">Annual Run Rate</p>
        </div>
        <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-4 text-center">
          <p className="text-3xl font-bold text-violet-400">{data.unique_paying_users}</p>
          <p className="text-xs text-[var(--isp-muted)] mt-1">Paying Users</p>
        </div>
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 text-center">
          <p className="text-3xl font-bold text-amber-400">{data.penetration_rate}%</p>
          <p className="text-xs text-[var(--isp-muted)] mt-1">User Penetration</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-3 text-center">
          <p className="text-xl font-bold text-white">{data.active_subscriptions}</p>
          <p className="text-[10px] text-[var(--isp-muted)]">Active Subscriptions</p>
        </div>
        <div className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-3 text-center">
          <p className="text-xl font-bold text-white">{data.avg_addons_per_user}</p>
          <p className="text-[10px] text-[var(--isp-muted)]">Avg Add-ons/User</p>
        </div>
        <div className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-3 text-center">
          <p className={`text-xl font-bold ${data.overall_churn_rate > 10 ? 'text-red-400' : 'text-emerald-400'}`}>{data.overall_churn_rate}%</p>
          <p className="text-[10px] text-[var(--isp-muted)]">Overall Churn Rate</p>
        </div>
      </div>

      {breakdown.length > 0 && (
        <div className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-4">
          <h4 className="text-sm font-semibold text-white mb-4">Add-on Performance</h4>
          <div className="space-y-3">
            {breakdown.sort((a, b) => b.mrr_contribution - a.mrr_contribution).map(addon => {
              const maxMrr = Math.max(...breakdown.map(a => a.mrr_contribution), 1);
              return (
                <div key={addon.name} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">{addon.name}</span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-emerald-400 font-medium">${addon.mrr_contribution}/mo</span>
                      <span className="text-blue-400">{addon.active} active</span>
                      <span className={`${addon.churn_rate > 15 ? 'text-red-400' : 'text-slate-400'}`}>{addon.churn_rate}% churn</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-500" style={{ width: `${(addon.mrr_contribution / maxMrr * 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-4">
        <h4 className="text-sm font-semibold text-white mb-3">User Base</h4>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex justify-between text-xs text-[var(--isp-muted)] mb-1">
              <span>Monetization penetration</span>
              <span>{data.unique_paying_users} of {data.total_users} users</span>
            </div>
            <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full" style={{ width: `${Math.min(data.penetration_rate, 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
